/**
 * Medical Terminology Validation Service
 *
 * Validates medical terminology in documents and chunks.
 * Ensures clinical accuracy and identifies potential issues.
 * Supports both raw medical text and LLM-structured algorithm text.
 *
 * Chunking strategy (2024–2025 clinical RAG research):
 * - Structure-aware / adaptive chunking aligns to logical boundaries (sections, headers,
 *   clinical concepts) rather than fixed token counts, improving accuracy and relevance
 *   (e.g. MDPI Bioengineering 2025: adaptive 87% vs fixed 50% accuracy; 93% relevance).
 * - We use (1) markdown section boundaries for LLM-structured algorithm text, and
 *   (2) medical concept boundaries + guideline section headers for raw text, with
 *   sentence-boundary splits when a segment exceeds max size to avoid mid-sentence cuts.
 * - See: Comparative Evaluation of Advanced Chunking for RAG in LLMs for Clinical
 *   Decision Support (MDPI Bioengineering 2025); CLI-RAG hierarchical chunking.
 */

import { debugLog } from '../../utils/debugLog';

// Common medical term patterns
const MEDICAL_TERM_PATTERNS = {
  // ICD-10 codes
  icd10: /\b[A-Z]\d{2}(?:\.\d{1,4})?\b/g,
  
  // CPT codes
  cpt: /\b\d{5}(?:-\d{2})?\b/g,
  
  // Medication names (common prefixes/suffixes)
  medication: /\b(?:acyclovir|amoxicillin|aspirin|ibuprofen|metformin|lisinopril|atorvastatin)\b/gi,
  
  // Medical abbreviations
  abbreviations: /\b(?:BP|HR|RR|O2|CO2|EKG|ECG|CT|MRI|CBC|BMP|LFT)\b/gi,
  
  // Units of measurement
  units: /\b(?:mg|g|kg|ml|l|mcg|IU|units|mmHg|bpm|°F|°C)\b/gi,
};

// Medical concept boundaries for RAW text (where to break chunks).
// Includes clinical note sections and clinical practice guideline sections so chunks
// align to logical units (improves RAG accuracy per 2024–2025 clinical chunking research).
const MEDICAL_CONCEPT_BOUNDARIES = [
  // ----- Clinical note sections -----
  /(?:^|\n)\s*(?:DIAGNOSIS|DX|DIFFERENTIAL DIAGNOSIS|DDX)[:\s]*/gi,
  /(?:^|\n)\s*(?:TREATMENT|TX|PLAN|MANAGEMENT)[:\s]*/gi,
  /(?:^|\n)\s*(?:MEDICATIONS|MEDS|PRESCRIPTIONS)[:\s]*/gi,
  /(?:^|\n)\s*(?:TESTS|LABS|DIAGNOSTICS|IMAGING)[:\s]*/gi,
  /(?:^|\n)\s*(?:ASSESSMENT|A&P|ASSESSMENT AND PLAN)[:\s]*/gi,
  /(?:^|\n)\s*(?:HISTORY|HX|PAST MEDICAL HISTORY|PMH)[:\s]*/gi,
  /(?:^|\n)\s*(?:PHYSICAL EXAM|PE|EXAMINATION)[:\s]*/gi,
  /(?:^|\n)\s*(?:CHIEF COMPLAINT|CC|PRESENTING COMPLAINT)[:\s]*/gi,
  // ----- Clinical practice guideline sections -----
  /(?:^|\n)\s*(?:INDICATIONS|INDICATION)[:\s]*/gi,
  /(?:^|\n)\s*(?:CONTRAINDICATIONS|CONTRAINDICATION)[:\s]*/gi,
  /(?:^|\n)\s*(?:DOSING|DOSE|DOSAGE)[:\s]*/gi,
  /(?:^|\n)\s*(?:MONITORING|MONITOR)[:\s]*/gi,
  /(?:^|\n)\s*(?:ADVERSE EFFECTS|ADVERSE REACTIONS|SIDE EFFECTS)[:\s]*/gi,
  /(?:^|\n)\s*(?:DRUG INTERACTIONS|INTERACTIONS)[:\s]*/gi,
  /(?:^|\n)\s*(?:REFERENCES|BIBLIOGRAPHY|SOURCES)[:\s]*/gi,
  /(?:^|\n)\s*(?:KEY RECOMMENDATIONS|RECOMMENDATIONS|KEY MESSAGES)[:\s]*/gi,
  /(?:^|\n)\s*(?:ADMINISTRATION|HOW TO ADMINISTER)[:\s]*/gi,
  /(?:^|\n)\s*(?:BASELINE|BASELINE LABS|BASELINE TESTING|PRE-?TREATMENT)[:\s]*/gi,
  /(?:^|\n)\s*(?:BACKGROUND|INTRODUCTION|SUMMARY|OBJECTIVE)[:\s]*/gi,
  /(?:^|\n)\s*(?:PRECAUTIONS|WARNINGS|CAUTIONS)[:\s]*/gi,
];

/**
 * Validate medical terminology in text
 * @param {string} text - Text to validate
 * @returns {Object} Validation results
 */
export const validateMedicalTerminology = (text) => {
  const issues = [];
  const foundTerms = {
    icd10: [],
    cpt: [],
    medications: [],
    abbreviations: [],
  };

  // Check for medical codes
  const icd10Matches = text.match(MEDICAL_TERM_PATTERNS.icd10);
  if (icd10Matches) {
    foundTerms.icd10 = [...new Set(icd10Matches)];
  }

  const cptMatches = text.match(MEDICAL_TERM_PATTERNS.cpt);
  if (cptMatches) {
    foundTerms.cpt = [...new Set(cptMatches)];
  }

  // Check for potentially outdated or incorrect terminology
  const outdatedTerms = [
    /\bretarded\b/gi, // Outdated term
    /\binsane\b/gi,   // Outdated term
  ];

  outdatedTerms.forEach(pattern => {
    if (pattern.test(text)) {
      issues.push({
        type: 'outdated_terminology',
        severity: 'warning',
        message: 'Document contains potentially outdated medical terminology',
      });
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
    foundTerms,
    medicalTermCount: Object.values(foundTerms).reduce((sum, arr) => sum + arr.length, 0),
  };
};

/**
 * Detect if text is LLM-structured (contains markdown headers from structure preservation)
 * @param {string} text - Text to check
 * @returns {boolean} True if text appears to be LLM-structured
 */
const isStructuredText = (text) => {
  // Look for markdown headers that the LLM structure preservation produces
  const headerPattern = /^#{1,3}\s+/gm;
  const headers = text.match(headerPattern);
  return headers && headers.length >= 2;
};

/**
 * Chunk LLM-structured text by markdown sections.
 * Each ## section becomes one chunk, preserving complete algorithm logic units.
 * Adjacent small sections are merged to maintain embedding quality.
 * @param {string} text - Structured text with markdown headers
 * @param {number} maxChunkSize - Maximum chunk size in characters
 * @returns {Array<{text: string, index: number, start: number, end: number, conceptType: string, sectionTitle: string}>}
 */
const chunkStructuredText = (text, maxChunkSize = 6000) => {
  // Increased maxChunkSize for structured text — each logical section should stay intact
  const MIN_CHUNK_SIZE = 300;
  const chunks = [];
  let chunkIndex = 0;

  // Split on ## headers (keep the header with its content)
  const sectionPattern = /(?=^#{1,3}\s+)/gm;
  const sections = text.split(sectionPattern).filter(s => s.trim().length > 0);
  
  if (sections.length === 0) {
    // No sections found, treat as single chunk
    return [{
      text: text.trim(),
      index: 0,
      start: 0,
      end: text.length,
      conceptType: 'general',
      sectionTitle: null,
    }];
  }

  // Track positions in original text
  let currentPos = 0;
  const sectionData = sections.map(section => {
    const start = text.indexOf(section, currentPos);
    currentPos = start + section.length;
    
    // Extract section title from header
    const titleMatch = section.match(/^#{1,3}\s+(.+?)(?:\n|$)/);
    const title = titleMatch ? titleMatch[1].trim() : null;
    
    // Detect concept type from title
    const conceptType = title ? detectConceptTypeFromTitle(title) : 'general';
    
    return {
      text: section.trim(),
      start: start >= 0 ? start : 0,
      end: start >= 0 ? start + section.length : section.length,
      title,
      conceptType,
    };
  });

  // Merge small adjacent sections to maintain minimum chunk size
  let accumulated = null;
  
  for (let i = 0; i < sectionData.length; i++) {
    const section = sectionData[i];
    
    if (!accumulated) {
      accumulated = { ...section };
      continue;
    }
    
    // If accumulated + this section still fits in maxChunkSize, and accumulated is small, merge
    const combinedLength = accumulated.text.length + section.text.length + 2; // +2 for \n\n
    
    if (accumulated.text.length < MIN_CHUNK_SIZE && combinedLength <= maxChunkSize) {
      // Merge into accumulated
      accumulated.text = accumulated.text + '\n\n' + section.text;
      accumulated.end = section.end;
      // Keep the first section's title
    } else {
      // Emit accumulated as a chunk
      chunks.push({
        text: accumulated.text,
        index: chunkIndex++,
        start: accumulated.start,
        end: accumulated.end,
        conceptType: accumulated.conceptType,
        sectionTitle: accumulated.title,
      });
      accumulated = { ...section };
    }
  }
  
  // Emit last accumulated section
  if (accumulated) {
    // If it's too small and we have a previous chunk, merge into it
    if (accumulated.text.length < MIN_CHUNK_SIZE && chunks.length > 0) {
      const prev = chunks[chunks.length - 1];
      if (prev.text.length + accumulated.text.length + 2 <= maxChunkSize) {
        prev.text = prev.text + '\n\n' + accumulated.text;
        prev.end = accumulated.end;
      } else {
        chunks.push({
          text: accumulated.text,
          index: chunkIndex++,
          start: accumulated.start,
          end: accumulated.end,
          conceptType: accumulated.conceptType,
          sectionTitle: accumulated.title,
        });
      }
    } else {
      chunks.push({
        text: accumulated.text,
        index: chunkIndex++,
        start: accumulated.start,
        end: accumulated.end,
        conceptType: accumulated.conceptType,
        sectionTitle: accumulated.title,
      });
    }
  }

  // Helper: split long text into sub-chunks of at most maxChunkSize (split at sentence or hard cut)
  const splitLongText = (text, startOffset, conceptType, sectionTitle) => {
    const result = [];
    let pos = 0;
    while (pos < text.length) {
      const remainder = text.slice(pos);
      if (remainder.length <= maxChunkSize) {
        result.push({
          text: remainder.trim(),
          start: startOffset + pos,
          end: startOffset + text.length,
          conceptType,
          sectionTitle,
        });
        break;
      }
      const slice = remainder.slice(0, maxChunkSize);
      const lastSentence = slice.match(/\n[^\n]*\.\s*$/);
      const splitAt = lastSentence ? slice.length - lastSentence[0].length : maxChunkSize;
      const piece = remainder.slice(0, splitAt).trim();
      if (piece.length > 0) {
        result.push({
          text: piece,
          start: startOffset + pos,
          end: startOffset + pos + piece.length,
          conceptType,
          sectionTitle,
        });
      }
      pos += splitAt;
    }
    return result;
  };

  // Handle any chunks that exceed maxChunkSize (split at paragraph boundaries; split long paras)
  const finalChunks = [];
  let finalIndex = 0;
  
  for (const chunk of chunks) {
    if (chunk.text.length <= maxChunkSize) {
      finalChunks.push({ ...chunk, index: finalIndex++ });
    } else {
      // Split oversized chunk at paragraph boundaries
      const paragraphs = chunk.text.split(/\n\n+/);
      let buffer = '';
      let bufferStart = chunk.start;
      let posInChunk = 0;
      
      for (const para of paragraphs) {
        const paraStartInChunk = chunk.text.indexOf(para, posInChunk);
        if (paraStartInChunk === -1) {
          posInChunk = chunk.text.length;
          continue;
        }
        if (para.length > maxChunkSize) {
          // Flush buffer first if any
          if (buffer.trim().length >= MIN_CHUNK_SIZE) {
            finalChunks.push({
              text: buffer.trim(),
              index: finalIndex++,
              start: bufferStart,
              end: bufferStart + buffer.length,
              conceptType: chunk.conceptType,
              sectionTitle: chunk.sectionTitle,
            });
          }
          buffer = '';
          const paraStart = chunk.start + paraStartInChunk;
          const subChunks = splitLongText(para, paraStart, chunk.conceptType, chunk.sectionTitle);
          for (const sc of subChunks) {
            finalChunks.push({ ...sc, index: finalIndex++ });
          }
          bufferStart = paraStart + para.length;
          posInChunk = paraStartInChunk + para.length;
          continue;
        }
        if (buffer.length + para.length + 2 > maxChunkSize && buffer.length >= MIN_CHUNK_SIZE) {
          finalChunks.push({
            text: buffer.trim(),
            index: finalIndex++,
            start: bufferStart,
            end: bufferStart + buffer.length,
            conceptType: chunk.conceptType,
            sectionTitle: chunk.sectionTitle,
          });
          buffer = para;
          bufferStart = chunk.start + paraStartInChunk;
        } else {
          buffer = buffer ? buffer + '\n\n' + para : para;
        }
        posInChunk = paraStartInChunk + para.length;
      }
      
      if (buffer.trim().length > 0) {
        const trimmed = buffer.trim();
        if (trimmed.length > maxChunkSize) {
          const subChunks = splitLongText(trimmed, bufferStart, chunk.conceptType, chunk.sectionTitle);
          for (const sc of subChunks) {
            finalChunks.push({ ...sc, index: finalIndex++ });
          }
        } else {
          finalChunks.push({
            text: trimmed,
            index: finalIndex++,
            start: bufferStart,
            end: chunk.end,
            conceptType: chunk.conceptType,
            sectionTitle: chunk.sectionTitle,
          });
        }
      }
    }
  }

  return finalChunks.filter(c => c.text.length > 0);
};

/**
 * Detect concept type from a section title (used for structured text)
 * @param {string} title - Section title
 * @returns {string} Concept type
 */
const detectConceptTypeFromTitle = (title) => {
  if (/\b(?:scor(?:e|ing)|criteria|index|scale|Wells|PESI|PERC)\b/i.test(title)) return 'scoring';
  if (/\b(?:algorithm|decision|pathway|flowchart|workflow)\b/i.test(title)) return 'algorithm';
  if (/\b(?:treatment|therapy|regimen|management|anticoagulat)\b/i.test(title)) return 'treatment';
  if (/\b(?:diagnos|assessment|evaluation|workup)\b/i.test(title)) return 'diagnosis';
  if (/\b(?:drug|medicat|dosage?|dosing|pharmaco)\b/i.test(title)) return 'medication';
  if (/\b(?:test|lab|imaging|x-?ray|CT|MRI)\b/i.test(title)) return 'test';
  if (/\b(?:interpret|result|classif|risk\s+stratif)\b/i.test(title)) return 'interpretation';
  if (/\b(?:pearl|pitfall|advice|note|caution|warning)\b/i.test(title)) return 'clinical_pearl';
  
  return 'general';
};

// Cap text length for boundary search to avoid long runtimes / ReDoS (regex matchAll on 100k+ chars can hang)
const BOUNDARY_SEARCH_MAX_LEN = 50000;

/**
 * Find medical concept boundaries in text
 * @param {string} text - Text to analyze
 * @returns {Array<number>} Array of indices where medical concepts begin
 */
export const findMedicalConceptBoundaries = (text) => {
  const boundaries = [0];
  const searchLen = Math.min(text.length, BOUNDARY_SEARCH_MAX_LEN);
  const searchText = searchLen < text.length ? text.slice(0, searchLen) : text;
  const boundarySet = new Set([0]);

  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: findMedicalConceptBoundaries start, searchLen=', searchLen);
  }
  MEDICAL_CONCEPT_BOUNDARIES.forEach((pattern, idx) => {
    pattern.lastIndex = 0;
    try {
      const matches = [...searchText.matchAll(pattern)];
      matches.forEach(match => {
        if (match.index !== undefined && !boundarySet.has(match.index)) {
          boundarySet.add(match.index);
          boundaries.push(match.index);
        }
      });
    } catch (_) {
      // Skip pattern if it throws (e.g. out of memory / backtrack limit)
    }
  });

  const numberedListPattern = /(?:^|\n)\s*\d+[.)]\s+/gm;
  numberedListPattern.lastIndex = 0;
  try {
    const listMatches = [...searchText.matchAll(numberedListPattern)];
    listMatches.forEach(match => {
      if (match.index !== undefined && !boundarySet.has(match.index)) {
        boundarySet.add(match.index);
        boundaries.push(match.index);
      }
    });
  } catch (_) {
    // Skip if regex throws
  }

  if (searchLen < text.length) {
    boundaries.push(searchLen);
  }
  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: findMedicalConceptBoundaries done, boundaries=', boundaries.length);
  }
  return boundaries.sort((a, b) => a - b);
};

/**
 * Chunk text at medical concept boundaries (structure-aware / adaptive for medical docs).
 * Automatically detects if text is LLM-structured and uses appropriate chunking strategy.
 * @param {string} text - Text to chunk
 * @param {number} maxChunkSize - Maximum chunk size in characters (~500 words / 2500 chars for raw text is supported by clinical RAG research; structured text uses larger cap to keep sections intact)
 * @param {number} overlap - Overlap size in characters (10–20% of chunk size helps context continuity)
 * @returns {Array<{text: string, index: number, start: number, end: number, conceptType?: string}>} Chunks
 */
export const chunkAtMedicalBoundaries = (text, maxChunkSize = 2500, overlap = 400) => {
  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: chunkAtMedicalBoundaries start, text length=', text.length);
  }
  // Check if this is LLM-structured text (has markdown headers)
  if (isStructuredText(text)) {
    if (typeof console !== 'undefined' && console.log) {
      console.log('[embed] chunking: using structured-text path');
    }
    debugLog('[MedicalChunking] Detected structured text, using section-aware chunking');
    // Use larger max chunk size for structured text to keep logical sections intact (algorithms, scoring, treatment pathways)
    return chunkStructuredText(text, Math.max(maxChunkSize, 6000));
  }

  // Fall back to boundary-based chunking for raw text (clinical notes, guidelines)
  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: using raw-text boundary path');
  }
  debugLog('[MedicalChunking] Using raw text boundary-based chunking');
  
  // Minimum useful chunk size for embedding quality (roughly 1-2 sentences)
  const MIN_CHUNK_SIZE = 200;

  const boundaries = findMedicalConceptBoundaries(text);
  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: building merged boundaries and chunks');
  }
  const chunks = [];
  let chunkIndex = 0;

  // Merge adjacent boundaries that would create chunks smaller than MIN_CHUNK_SIZE
  const mergedBoundaries = [boundaries[0]];
  for (let i = 1; i < boundaries.length; i++) {
    const prevBoundary = mergedBoundaries[mergedBoundaries.length - 1];
    const sectionSize = boundaries[i] - prevBoundary;
    
    // Only keep this boundary if the section it creates is large enough
    if (sectionSize >= MIN_CHUNK_SIZE) {
      mergedBoundaries.push(boundaries[i]);
    }
    // Otherwise skip it — the previous boundary will extend to the next one
  }
  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: merged boundaries count=', mergedBoundaries.length);
  }

  for (let i = 0; i < mergedBoundaries.length; i++) {
    if (typeof console !== 'undefined' && console.log && (i < 3 || i % 10 === 0 || i === mergedBoundaries.length - 1)) {
      console.log('[embed] chunking: segment', i + 1, '/', mergedBoundaries.length);
    }
    const start = mergedBoundaries[i];
    const nextBoundary = mergedBoundaries[i + 1] || text.length;
    const segmentLen = nextBoundary - start;

    // If this section is smaller than maxChunkSize, use it as one chunk
    if (segmentLen <= maxChunkSize) {
      const chunkText = text.substring(start, nextBoundary).trim();
      if (chunkText.length >= MIN_CHUNK_SIZE || mergedBoundaries.length <= 2) {
        // Accept the chunk if it meets minimum size, or if there are very few boundaries
        chunks.push({
          text: chunkText,
          index: chunkIndex++,
          start,
          end: nextBoundary,
          conceptType: detectConceptType(text.substring(start, Math.min(start + 100, nextBoundary))),
        });
      } else if (chunks.length > 0) {
        // Merge undersized trailing chunk into the previous one
        const prev = chunks[chunks.length - 1];
        prev.text = text.substring(prev.start, nextBoundary).trim();
        prev.end = nextBoundary;
      } else {
        // First chunk and it's small — keep it anyway
        chunks.push({
          text: chunkText,
          index: chunkIndex++,
          start,
          end: nextBoundary,
          conceptType: detectConceptType(text.substring(start, Math.min(start + 100, nextBoundary))),
        });
      }
    } else {
      // Section is too large, split it further. Enforce minimum advance so we don't crawl (e.g. advance by only overlap).
      const minAdvance = Math.max(overlap + 500, Math.floor(maxChunkSize * 0.4));
      let sectionStart = start;
      let innerCount = 0;
      const maxInnerIterations = 500; // safety cap for one segment
      while (sectionStart < nextBoundary) {
        if (innerCount >= maxInnerIterations) {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[embed] chunking: segment', i + 1, 'hit safety cap', maxInnerIterations, '- breaking');
          }
          break;
        }
        if (typeof console !== 'undefined' && console.log && (innerCount < 2 || innerCount % 15 === 0)) {
          console.log('[embed] chunking: segment', i + 1, 'inner iteration', innerCount + 1);
        }
        innerCount += 1;
        const sectionEnd = Math.min(sectionStart + maxChunkSize, nextBoundary);
        
        // Try to break at sentence boundary within this section
        const sectionText = text.substring(sectionStart, sectionEnd);
        const lastSentence = sectionText.lastIndexOf('. ');
        const lastParagraph = sectionText.lastIndexOf('\n\n');
        const breakPoint = Math.max(lastSentence, lastParagraph);
        
        let actualEnd = breakPoint > maxChunkSize * 0.5
          ? sectionStart + breakPoint + 1
          : sectionEnd;
        // Ensure we advance by at least minAdvance so we don't need hundreds of iterations per segment
        if (actualEnd - sectionStart < minAdvance && sectionEnd < nextBoundary) {
          actualEnd = Math.min(sectionStart + minAdvance, nextBoundary);
        }
        
        chunks.push({
          text: text.substring(sectionStart, actualEnd).trim(),
          index: chunkIndex++,
          start: sectionStart,
          end: actualEnd,
          conceptType: detectConceptType(text.substring(sectionStart, Math.min(sectionStart + 100, actualEnd))),
        });
        
        sectionStart = actualEnd - overlap;
      }
    }
  }

  if (typeof console !== 'undefined' && console.log) {
    console.log('[embed] chunking: chunkAtMedicalBoundaries done, chunks=', chunks.length);
  }
  return chunks.filter(chunk => chunk.text.length > 0);
};

// Max snippet length for concept detection
const DETECT_CONCEPT_SNIPPET_MAX_LEN = 300;

// String-based section keywords (one array per label; order matches MEDICAL_CONCEPT_BOUNDARIES).
// Used instead of regex in detectConceptType to avoid ReDoS / hangs.
const CONCEPT_KEYWORDS = [
  ['DIAGNOSIS', 'DX', 'DIFFERENTIAL DIAGNOSIS', 'DDX'],
  ['TREATMENT', 'TX', 'PLAN', 'MANAGEMENT'],
  ['MEDICATIONS', 'MEDS', 'PRESCRIPTIONS'],
  ['TESTS', 'LABS', 'DIAGNOSTICS', 'IMAGING'],
  ['ASSESSMENT', 'A&P', 'ASSESSMENT AND PLAN'],
  ['HISTORY', 'HX', 'PAST MEDICAL HISTORY', 'PMH'],
  ['PHYSICAL EXAM', 'PE', 'EXAMINATION'],
  ['CHIEF COMPLAINT', 'CC', 'PRESENTING COMPLAINT'],
  ['INDICATIONS', 'INDICATION'],
  ['CONTRAINDICATIONS', 'CONTRAINDICATION'],
  ['DOSING', 'DOSE', 'DOSAGE'],
  ['MONITORING', 'MONITOR'],
  ['ADVERSE EFFECTS', 'ADVERSE REACTIONS', 'SIDE EFFECTS'],
  ['DRUG INTERACTIONS', 'INTERACTIONS'],
  ['REFERENCES', 'BIBLIOGRAPHY', 'SOURCES'],
  ['KEY RECOMMENDATIONS', 'RECOMMENDATIONS', 'KEY MESSAGES'],
  ['ADMINISTRATION', 'HOW TO ADMINISTER'],
  ['BASELINE', 'BASELINE LABS', 'BASELINE TESTING', 'PRE-TREATMENT', 'PRETREATMENT'],
  ['BACKGROUND', 'INTRODUCTION', 'SUMMARY', 'OBJECTIVE'],
  ['PRECAUTIONS', 'WARNINGS', 'CAUTIONS'],
];

const DETECT_CONCEPT_LABELS = [
  'diagnosis', 'treatment', 'medication', 'test', 'assessment', 'history', 'physical_exam', 'chief_complaint',
  'indications', 'contraindications', 'dosing', 'monitoring', 'adverse_effects', 'drug_interactions',
  'references', 'recommendations', 'administration', 'baseline', 'background', 'precautions',
];

/**
 * Detect the type of medical concept in a text snippet using string matching only (no regex in hot path to avoid ReDoS).
 * @param {string} snippet - Text snippet (only first DETECT_CONCEPT_SNIPPET_MAX_LEN chars are used)
 * @returns {string} Concept type
 */
const detectConceptType = (snippet) => {
  if (typeof snippet !== 'string') return 'general';
  const capped = snippet.length > DETECT_CONCEPT_SNIPPET_MAX_LEN
    ? snippet.slice(0, DETECT_CONCEPT_SNIPPET_MAX_LEN)
    : snippet;
  const upper = capped.toUpperCase();
  const lines = upper.split('\n');
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    for (let i = 0; i < CONCEPT_KEYWORDS.length; i++) {
      for (const kw of CONCEPT_KEYWORDS[i]) {
        if (t === kw || t.startsWith(kw + ' ') || t.startsWith(kw + ':') || t.startsWith(kw + '\t')) {
          return DETECT_CONCEPT_LABELS[i];
        }
      }
    }
  }
  return 'general';
};

/**
 * Check if a chunk contains complete medical concepts
 * @param {string} chunkText - Chunk text
 * @returns {boolean} True if chunk appears complete
 */
export const isCompleteMedicalConcept = (chunkText) => {
  // Check if chunk ends with proper punctuation (sentence/paragraph end)
  const trimmed = chunkText.trim();
  if (!trimmed) return false;
  
  // Ends with sentence punctuation
  if (/[.!?]\s*$/.test(trimmed)) return true;
  
  // Ends with paragraph break
  if (/\n\n\s*$/.test(trimmed)) return true;
  
  // Ends with list item (numbered or bulleted)
  if (/[•\-*]\s*$/.test(trimmed)) return true;
  
  return false;
};

const medicalTerminologyService = {
  validateMedicalTerminology,
  findMedicalConceptBoundaries,
  chunkAtMedicalBoundaries,
  isCompleteMedicalConcept,
};

export default medicalTerminologyService;
